import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import type { ComponentProps, ComponentType } from 'react'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

type MonacoEditorProps = ComponentProps<
  typeof import('@monaco-editor/react')['default']
>

const MonacoEditorLazy = lazy(
  () =>
    import('@monaco-editor/react') as Promise<{
      default: typeof import('@monaco-editor/react')['default']
    }>,
)

export type SupportedLanguage = 'python' | 'javascript'

export interface LanguageCodeData {
  definitionCode: string
  helperCode?: string
  testCode?: string
  testExpectedOutput?: string
  lastValidatedAt?: string | null
}

interface MultiLanguageCodeEditorProps {
  value: Record<string, LanguageCodeData>
  onChange: (value: Record<string, LanguageCodeData>) => void
  languages: SupportedLanguage[]
  showValidation?: boolean // Show test code + expected output fields
  showHelperCode?: boolean // Show separate helper code editor (templates only)
  codeLabel?: string // Label for the main code editor
  onValidate?: (
    language: string,
  ) => Promise<{ success: boolean; error?: string }>
  validating?: boolean
  height?: string
}

function EditorSkeleton() {
  return (
    <div className="h-full w-full space-y-2 p-4">
      <div className="flex gap-4">
        <div className="flex w-8 flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton
            <Skeleton key={`line-${i}`} className="h-4 w-6" />
          ))}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="ml-4 h-4 w-40" />
          <Skeleton className="ml-4 h-4 w-24" />
        </div>
      </div>
    </div>
  )
}

function MonacoWrapper({
  MonacoComponent,
  isClient,
  monacoProps,
}: {
  MonacoComponent: ComponentType<MonacoEditorProps>
  isClient: boolean
  monacoProps: MonacoEditorProps
}) {
  return (
    <Suspense fallback={<EditorSkeleton />}>
      {isClient ? <MonacoComponent {...monacoProps} /> : <EditorSkeleton />}
    </Suspense>
  )
}

export function MultiLanguageCodeEditor({
  value,
  onChange,
  languages,
  showValidation = false,
  showHelperCode = false,
  codeLabel = 'Code',
  onValidate,
  validating = false,
  height = '250px',
}: MultiLanguageCodeEditorProps) {
  const [activeTab, setActiveTab] = useState<SupportedLanguage>(languages[0])
  const [isClient, setIsClient] = useState(false)

  // Detect if we're on the client side
  useEffect(() => {
    setIsClient(true)
  }, [])

  // Detect dark mode
  const isDarkMode =
    typeof window !== 'undefined'
      ? document.documentElement.classList.contains('dark')
      : false

  const activeData = value[activeTab] || { definitionCode: '' }

  const handleDefinitionCodeChange = (newCode: string | undefined) => {
    onChange({
      ...value,
      [activeTab]: {
        ...activeData,
        definitionCode: newCode || '',
      },
    })
  }

  const handleHelperCodeChange = (newCode: string | undefined) => {
    onChange({
      ...value,
      [activeTab]: {
        ...activeData,
        helperCode: newCode || '',
      },
    })
  }

  const handleTestCodeChange = (newTestCode: string) => {
    onChange({
      ...value,
      [activeTab]: {
        ...activeData,
        testCode: newTestCode,
      },
    })
  }

  const handleExpectedOutputChange = (newExpectedOutput: string) => {
    onChange({
      ...value,
      [activeTab]: {
        ...activeData,
        testExpectedOutput: newExpectedOutput,
      },
    })
  }

  const handleValidate = async () => {
    if (onValidate) {
      await onValidate(activeTab)
    }
  }

  // Get validation status for each tab
  const getValidationStatus = (
    lang: SupportedLanguage,
  ): 'valid' | 'invalid' | 'unknown' => {
    const data = value[lang]
    if (!data?.lastValidatedAt) return 'unknown'
    // If lastValidatedAt exists, it means validation passed at some point
    return 'valid'
  }

  const formatRelativeTime = (isoDate: string): string => {
    const date = new Date(isoDate)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins} min ago`
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  }

  return (
    <div className="space-y-3">
      {/* Language Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {languages.map((lang) => {
          const status = getValidationStatus(lang)
          const isActive = activeTab === lang
          return (
            <button
              key={lang}
              type="button"
              onClick={() => setActiveTab(lang)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {lang === 'python' ? 'Python' : 'JavaScript'}
              {showValidation && status === 'valid' && (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
              )}
              {showValidation && status === 'invalid' && (
                <XCircle className="h-3.5 w-3.5 text-destructive" />
              )}
            </button>
          )
        })}
      </div>

      {/* Main Code Editor */}
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          {codeLabel}
        </label>
        <div
          className="border border-border rounded-md overflow-hidden"
          style={{ height }}
        >
          <MonacoWrapper
            MonacoComponent={MonacoEditorLazy}
            isClient={isClient}
            monacoProps={{
              height: '100%',
              language: activeTab,
              value: activeData.definitionCode,
              theme: isDarkMode ? 'vs-dark' : 'vs',
              onChange: handleDefinitionCodeChange,
              options: {
                automaticLayout: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 13,
                fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
                tabSize: 2,
              },
            }}
          />
        </div>
        {showHelperCode && (
          <p className="mt-1 text-xs text-muted-foreground">
            Class/struct definition shown to users in the editor
          </p>
        )}
      </div>

      {/* Helper Code Editor */}
      {showHelperCode && (
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Helper Code
          </label>
          <div
            className="border border-border rounded-md overflow-hidden"
            style={{ height: '150px' }}
          >
            <MonacoWrapper
              MonacoComponent={MonacoEditorLazy}
              isClient={isClient}
              monacoProps={{
                height: '100%',
                language: activeTab,
                value: activeData.helperCode || '',
                theme: isDarkMode ? 'vs-dark' : 'vs',
                onChange: handleHelperCodeChange,
                options: {
                  automaticLayout: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                  fontFamily:
                    'Menlo, Monaco, Consolas, "Courier New", monospace',
                  tabSize: 2,
                },
              }}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Internal code injected at runtime only (serializers, utilities)
          </p>
        </div>
      )}

      {/* Validation Section */}
      {showValidation && (
        <div className="space-y-3 border-t border-border pt-3">
          <h4 className="text-sm font-medium">Validation Test</h4>

          {/* Test Code Editor */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Test Code
            </label>
            <div
              className="border border-border rounded-md overflow-hidden"
              style={{ height: '120px' }}
            >
              <MonacoWrapper
                MonacoComponent={MonacoEditorLazy}
                isClient={isClient}
                monacoProps={{
                  height: '100%',
                  language: activeTab,
                  value: activeData.testCode || '',
                  theme: isDarkMode ? 'vs-dark' : 'vs',
                  onChange: (val) => handleTestCodeChange(val || ''),
                  options: {
                    automaticLayout: true,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 13,
                    fontFamily:
                      'Menlo, Monaco, Consolas, "Courier New", monospace',
                    tabSize: 2,
                  },
                }}
              />
            </div>
          </div>

          {/* Expected Output */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Expected Output
            </label>
            <Textarea
              value={activeData.testExpectedOutput || ''}
              onChange={(e) => handleExpectedOutputChange(e.target.value)}
              placeholder="Expected stdout from test code..."
              className="min-h-[60px] font-mono text-sm"
            />
          </div>

          {/* Validate Button */}
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleValidate}
              disabled={validating || !onValidate || !activeData.definitionCode}
            >
              {validating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Validating...
                </>
              ) : (
                'Run Test'
              )}
            </Button>
            {activeData.lastValidatedAt && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                Passed {formatRelativeTime(activeData.lastValidatedAt)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
